import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';

import { PortMessage } from '../../../../types/PortMessage';
import { ReadOnlyField } from '../../../cdl/ReadOnlyField/ReadOnlyField';
import { queries } from '../../../common/api/queryKeys/queries';
import { startOfTimeZoneDayAsUtcIso } from '../../../common/helpers/dateFns.helper';
import { formatPort } from '../../../common/helpers/formatPort.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { Schedule, useSchedules } from '../../../common/hooks/useSchedules';
import { FormikPortDetailSelect } from '../../../port/portSelect/FormikPortDetailSelect';
import { OfferModel } from '../../model/OfferModel';

interface PortDisplayProps {
    offer: OfferModel;
    onDeliveryDetailsChange: (portId: string, dateDelivery: string) => void;
}

export const PortDisplay = (props: PortDisplayProps) => {
    const role = useRole();
    const { setFieldError, setFieldValue } = useFormikContext<OfferModel>();
    const supplierSettingsQuery = useQuery({
        ...queries.suppliers.detail(props.offer.supplierId)._ctx.settings,
        enabled: role.isSupplier() && !!props.offer.supplierId,
    });
    const { schedules } = useSchedules({
        imo: props.offer.vessel.imo,
        offerId: props.offer.id,
        scheduleSharingEnabled:
            props.offer.scheduleSharingEnabled === true && supplierSettingsQuery.data?.scheduleSharingEnabled === true,
    });

    const onPortSelect = (port: PortMessage, scheduleRank?: number) => {
        setFieldValue('agentId', undefined, false);
        setFieldValue('agentInformation', undefined, false);

        const matchingSchedule = schedules.find(
            (scheduleEntry: Schedule) => scheduleEntry.port?.id === port.id && scheduleEntry.rank === scheduleRank
        );

        if (!matchingSchedule) {
            props.onDeliveryDetailsChange(port.id, props.offer.dateDelivery);
            return;
        }

        const dateDelivery = startOfTimeZoneDayAsUtcIso(matchingSchedule.eta, matchingSchedule.timeZoneId);
        setFieldValue('dateDelivery', dateDelivery, false);
        setFieldError('dateDelivery', undefined);
        props.onDeliveryDetailsChange(port.id, dateDelivery);
    };

    const supplierCanChangePort =
        role.isSupplier() && (props.offer.isOrder() || supplierSettingsQuery.data?.multipleOffersEnabled === true);

    if (role.isCustomer() || supplierCanChangePort) {
        return (
            <FormikPortDetailSelect
                name="portId"
                schedule={schedules}
                vesselId={props.offer.vesselId}
                onSelect={onPortSelect}
            />
        );
    }

    return <ReadOnlyField value={formatPort({ port: props.offer.port })} label={translate('offer.edit.lubes.port')} />;
};
